import { Request } from "@/data/Requests";
import {
  OFF_DUTY_PATIENT_ID,
  PURPOSE_JOIN_SEPARATOR,
  REQUEST_STATUS_OPTIONS,
  SELECT_MODAL_OTHERS,
} from "@/utils/constants";

/**
 * Whether procedures on this request can be edited (not off-duty, not terminal status).
 */
export const canEditRequestProcedures = (request: Request): boolean => {
  if (!request.id) return false;
  if (request.patient_id === OFF_DUTY_PATIENT_ID) return false;
  const s = request.status;
  if (
    s === REQUEST_STATUS_OPTIONS.CANCELLED ||
    s === REQUEST_STATUS_OPTIONS.DECLINED
  ) {
    return false;
  }
  return true;
};

export interface ParsedPurposeForEdit {
  /** Ordered procedure tokens as stored in form state (comma-joined `purpose` in manual booking). */
  segments: string[];
  othersDetail: string | null;
}

/**
 * Parses stored `request.purpose` into multi-select segments and optional "Others" detail.
 */
export const parsePurposeForEdit = (
  purpose: string | null,
): ParsedPurposeForEdit => {
  if (!purpose?.trim()) {
    return { segments: [], othersDetail: null };
  }

  const raw = purpose
    .split(PURPOSE_JOIN_SEPARATOR)
    .map((p) => p.trim())
    .filter(Boolean);

  const segments: string[] = [];
  let othersDetail: string | null = null;
  const othersPrefix = `${SELECT_MODAL_OTHERS}:`;

  for (const part of raw) {
    if (part.startsWith(othersPrefix)) {
      othersDetail = part.slice(othersPrefix.length).trim();
      if (!segments.includes(SELECT_MODAL_OTHERS)) {
        segments.push(SELECT_MODAL_OTHERS);
      }
    } else if (part === SELECT_MODAL_OTHERS) {
      if (!segments.includes(SELECT_MODAL_OTHERS)) {
        segments.push(SELECT_MODAL_OTHERS);
      }
    } else {
      segments.push(part);
    }
  }

  return { segments, othersDetail };
};

/**
 * Builds the persisted `purpose` string from multi-select state (aligned with manual booking).
 */
export const buildPurposeString = (
  segments: string[],
  othersDetail: string | null,
): string => {
  const parts = segments.map((seg) =>
    seg === SELECT_MODAL_OTHERS
      ? othersDetail?.trim()
        ? `${SELECT_MODAL_OTHERS}: ${othersDetail.trim()}`
        : SELECT_MODAL_OTHERS
      : seg,
  );
  return parts.filter(Boolean).join(PURPOSE_JOIN_SEPARATOR);
};
